/**
 * Ancoras — os objetos clicaveis da fazenda.
 *
 * Cada ancora e um lugar fixo do cenario onde tarefas acontecem. A lista de
 * ancoras validas esta na secao 6 e e fechada: nao invente ancora nova.
 *
 * Montadas com os modelos da AssetHunts (ver assets/models/CREDITOS.md), na
 * grade nativa de 2 unidades em que foram desenhados.
 *
 * Estados "adormecidos" (construcao ainda nao feita) ficam em cinza
 * dessaturado — o contraste com a versao colorida e a recompensa visual
 * (secao 12 e D10).
 */

import * as THREE from 'three';
import { clonar, TILE, ALTURA_SOLO } from './Modelos.js';

export const ANCORAS_VALIDAS = [
  'canteiro', 'cerca', 'galinheiro', 'poco', 'composteira', 'moinho',
  'estufa', 'roda_dagua', 'ponte', 'cozinha', 'celeiro', 'bancada',
  'ceu', 'caderno',
];

/**
 * Onde cada ancora fica no terreno, na grade de 2 unidades.
 * O caminho de terra corre em X = 0; as construcoes ficam dos dois lados dele.
 */
export const POSICOES = {
  canteiro: [-8, 0, 5],
  cozinha: [-8, 0, -8],
  celeiro: [8, 0, -7],
  bancada: [1, 0, 0],
  caderno: [-1, 0, 11],
  ceu: [0, 16, -4],

  cerca: [-6, 0, 12],
  galinheiro: [9, 0, 5],
  poco: [4, 0, -3],
  composteira: [-12, 0, -2],
  moinho: [-3, 0, -12],
  estufa: [12, 0, 1],
  roda_dagua: [12, 0, 9],
  ponte: [4, 0, 9],
};

// ---------------------------------------------------------------------------
// Layout dos buracos do canteiro
// ---------------------------------------------------------------------------
//
// Fica aqui, e nao dentro do motor, porque duas partes precisam do MESMO
// calculo: o motor `contar` desenha os buracos, e a Fazenda desenha as plantas
// que nascem deles. Se as contas divergirem, a planta nasce fora do buraco.

/** D3 — alvo grande para o dedo, com folga entre um buraco e outro. */
export const ESPACAMENTO_BURACO = 2.0;

/**
 * Posicoes dos buracos de um canteiro, centralizadas.
 * Cada fileira e centralizada pela quantidade REAL de buracos nela.
 * @returns {{x: number, z: number}[]}
 */
export function posicoesDeBuracos(base, quantidade, porLinha = 5) {
  const linhas = Math.ceil(quantidade / porLinha);
  const posicoes = [];

  for (let i = 0; i < quantidade; i++) {
    const coluna = i % porLinha;
    const linha = Math.floor(i / porLinha);
    const nestaLinha = Math.min(porLinha, quantidade - linha * porLinha);
    const larguraLinha = (nestaLinha - 1) * ESPACAMENTO_BURACO;

    posicoes.push({
      x: base.x - larguraLinha / 2 + coluna * ESPACAMENTO_BURACO,
      z: base.z - ((linhas - 1) * ESPACAMENTO_BURACO) / 2 + linha * ESPACAMENTO_BURACO,
    });
  }

  return posicoes;
}

// ---------------------------------------------------------------------------
// Helpers de montagem
// ---------------------------------------------------------------------------

const COR_ADORMECIDA = new THREE.Color(0xb9c4bb);

/**
 * Construcao ainda nao feita: fantasma claro e translucido.
 *
 * Antes era cinza macico, e o resultado era um amontoado de blocos cinzas
 * pesados que dominavam a tela e nao pareciam construcao nenhuma. Translucido
 * le como "isto ainda vai existir", e deixa a construcao pronta ganhar o
 * contraste que a secao 12 pede.
 */
function adormecer(objeto) {
  objeto.traverse((filho) => {
    if (!filho.isMesh) return;
    // Clona o material para nao apagar a cor dos outros clones do mesmo modelo.
    filho.material = filho.material.clone();
    filho.material.map = null;
    filho.material.color = COR_ADORMECIDA.clone();
    filho.material.transparent = true;
    filho.material.opacity = 0.4;
    filho.material.depthWrite = false;
    filho.material.needsUpdate = true;
  });
}

/**
 * Coloca um modelo no grupo.
 *
 * Por padrao APOIA o modelo: mede a caixa dele e desce o tanto necessario para
 * a base encostar em y=0 do grupo. Sem isto cada modelo precisaria de um ajuste
 * de altura escrito na mao — o celeiro comeca 1,31 abaixo da origem, o silo
 * comeca em zero, e chutar isso foi o que deixou predios enterrados e flutuando
 * na primeira montagem.
 */
function por(grupo, papel, { x = 0, y = 0, z = 0, giro = 0, escala = 1, apoiar = true } = {}) {
  const modelo = clonar(papel);
  modelo.scale.setScalar(escala);
  modelo.rotation.y = giro;

  let baseY = 0;
  if (apoiar) {
    modelo.updateMatrixWorld(true);
    baseY = new THREE.Box3().setFromObject(modelo).min.y;
  }

  modelo.position.set(x, y - baseY, z);
  grupo.add(modelo);
  return modelo;
}

const QUARTO = Math.PI / 2;

/** Fileira de cerca ao longo de X. As pecas medem 2 unidades. */
function fileiraDeCerca(grupo, quantas, { z = 0, giro = 0, modelo = 'cerca_a' } = {}) {
  const largura = (quantas - 1) * TILE;
  for (let i = 0; i < quantas; i++) {
    por(grupo, modelo, { x: -largura / 2 + i * TILE, z, giro });
  }
}

// ---------------------------------------------------------------------------
// Construtores de cada ancora
// ---------------------------------------------------------------------------

function fazerCanteiro(grupo) {
  // Talhao de terra arada: 4x3 blocos, alinhados a grade do chao.
  //
  // Os blocos de terra tem a mesma altura dos blocos do terreno, entao eles
  // OCUPAM o lugar da grama em vez de ficar por cima. Um decimo de unidade
  // acima evita disputa de profundidade com a tampa de grama que esta embaixo.
  const ALTURA = -ALTURA_SOLO + 0.1;

  for (let linha = 0; linha < 3; linha++) {
    for (let coluna = 0; coluna < 4; coluna++) {
      por(grupo, 'terra_arada', {
        x: -3 + coluna * TILE,
        y: ALTURA,
        z: -2 + linha * TILE,
        apoiar: false, // e um bloco de terreno, nao um objeto em cima dele
      });
    }
  }

  por(grupo, 'placa_milho', { x: -4.6, z: -3.2, giro: 0.4, escala: 1.4 });
  por(grupo, 'regador', { x: 4.4, z: -2.4, giro: -0.5, escala: 1.4 });
}

function fazerCerca(grupo) {
  fileiraDeCerca(grupo, 6);
}

function fazerGalinheiro(grupo) {
  por(grupo, 'celeiro', { escala: 0.42 });
  // Cercadinho na frente, onde as galinhas ciscam.
  fileiraDeCerca(grupo, 3, { z: 4.5 });
  por(grupo, 'caixote', { x: -2.6, z: 3.4 });
}

function fazerPoco(grupo) {
  por(grupo, 'balde', { escala: 1.6 });
  por(grupo, 'pedra_a', { x: 1.6, z: 0.8 });
  por(grupo, 'pedra_b', { x: -1.5, z: -0.9, giro: 1.2 });
}

function fazerComposteira(grupo) {
  por(grupo, 'caixote', { x: -1.1 });
  por(grupo, 'barril', { x: 1.2, giro: 0.4 });
  por(grupo, 'toco', { z: 1.8, escala: 0.9 });
}

function fazerMoinho(grupo) {
  // O silo tem 12 unidades de altura: reduzido para nao dominar a ilha.
  por(grupo, 'silo', { escala: 0.6 });
}

function fazerEstufa(grupo) {
  // O pacote nao tem estufa. O mais proximo e um canteiro cercado com culturas
  // ja crescidas — a estufa e "o lugar onde as plantas ficam protegidas".
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 2; j++) {
      por(grupo, 'terra_arada', { x: -2 + i * TILE, z: -1 + j * TILE });
    }
  }
  por(grupo, 'girassol_3', { x: -2, z: -1 });
  por(grupo, 'girassol_3', { x: 0, z: 1 });
  por(grupo, 'brocolis_3', { x: 2, z: -1 });
  fileiraDeCerca(grupo, 3, { z: 2.6 });
}

function fazerRodaDagua(grupo) {
  // Sem roda d'agua no pacote: e um poco de agua com a roda sugerida pelo silo
  // pequeno ao lado. Trocar quando houver modelo proprio.
  for (let i = 0; i < 2; i++) {
    for (let j = 0; j < 2; j++) {
      por(grupo, 'agua', { x: -1 + i * TILE, z: -1 + j * TILE });
    }
  }
  por(grupo, 'silo', { x: 3.4, escala: 0.35 });
}

function fazerPonte(grupo) {
  por(grupo, 'ponte', { escala: 1.4 });
}

function fazerCozinha(grupo) {
  por(grupo, 'casa', { escala: 0.8 });
  por(grupo, 'caixa_correio', { x: 3.6, z: 2.4 });
  por(grupo, 'barril', { x: -3.4, z: 2.2 });
}

function fazerCeleiro(grupo) {
  por(grupo, 'celeiro', { escala: 0.75 });
  por(grupo, 'caixote', { x: 5.2, z: 3.0 });
  por(grupo, 'barril', { x: 6.2, z: 1.8, giro: 0.6 });
  por(grupo, 'caixote', { x: -5.4, z: 2.8, giro: 0.3 });
}

function fazerBancada(grupo) {
  por(grupo, 'caixote', { escala: 1.3 });
  por(grupo, 'pa', { x: 1.6, giro: 0.5 });
  por(grupo, 'foice', { x: -1.6, giro: -0.4 });
}

function fazerCaderno(grupo) {
  por(grupo, 'placa_madeira', { escala: 1.6 });
}

function fazerCeu(grupo) {
  por(grupo, 'nuvem_a', { x: -4, escala: 1.2 });
  por(grupo, 'nuvem_b', { x: 1.5, y: 1.2, z: 1.5, escala: 1.0 });
  por(grupo, 'nuvem_c', { x: 5.5, y: -0.6, z: -1, escala: 1.1 });
}

const CONSTRUTORES = {
  canteiro: fazerCanteiro,
  cerca: fazerCerca,
  galinheiro: fazerGalinheiro,
  poco: fazerPoco,
  composteira: fazerComposteira,
  moinho: fazerMoinho,
  estufa: fazerEstufa,
  roda_dagua: fazerRodaDagua,
  ponte: fazerPonte,
  cozinha: fazerCozinha,
  celeiro: fazerCeleiro,
  bancada: fazerBancada,
  ceu: fazerCeu,
  caderno: fazerCaderno,
};

/**
 * Cria a ancora pedida.
 * @param {string} nome  uma das ANCORAS_VALIDAS
 * @param {boolean} adormecida  true = cinza dessaturado, ainda nao construida
 */
export function criarAncora(nome, adormecida = false) {
  const construtor = CONSTRUTORES[nome];
  if (!construtor) {
    throw new Error(
      `FAZENDA: a ancora "${nome}" nao existe. ` +
        `Ancoras validas (secao 6): ${ANCORAS_VALIDAS.join(', ')}. ` +
        'Confira o campo "ancora" do JSON da tarefa.'
    );
  }

  const grupo = new THREE.Group();
  construtor(grupo);

  if (adormecida) adormecer(grupo);

  grupo.name = nome;
  grupo.userData.ancora = nome;
  grupo.userData.adormecida = adormecida;

  // Tudo se apoia na superficie do terreno, nao no fundo dos blocos.
  const [x, y, z] = POSICOES[nome];
  grupo.position.set(x, y + ALTURA_SOLO, z);
  return grupo;
}
